import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasswordInput from "../components/PasswordInput";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import { useAuth, API } from "../context/AuthContext";
import { getRememberMe, setRememberMe } from "../lib/consent";
import { validateBusinessWebsite } from "../lib/urls";
import { postLoginRedirect } from "../utils/postLoginRedirect";
import { useSeo } from "../lib/seo";
import { toast } from "sonner";
import SpecialtyPicker from "../components/SpecialtyPicker";

export default function Auth({ mode }) {
  const isSignup = mode === "signup";
  // Google sign-in is intentionally hidden from the public sign-in/signup
  // screens for now. The backend OAuth integration, credentials and the
  // /auth/callback route are all left intact — flip this to `true` to re-enable
  // the button, no other change needed.
  const GOOGLE_SIGNIN_ENABLED = false;
  useSeo({
    title: isSignup ? "Create your BookFreely account" : "Sign in to BookFreely",
    description: isSignup
      ? "Create a free BookFreely account to book services or list your business on the marketplace."
      : "Sign in to your BookFreely account to manage bookings, subscriptions, and payouts.",
    path: isSignup ? "/signup" : "/login",
    noindex: true,
  });
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "business" ? "business" : "customer";
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  // Business Phase-1 additions
  const [preferredName, setPreferredName] = useState("");
  const [providesMobile, setProvidesMobile] = useState(false); // "Do you provide mobile services?"
  const [hasStorefront, setHasStorefront] = useState(true);    // relevant when providesMobile=true (both?)
  // Iteration 55 — Mobile Services feature flag.
  const [mobileServicesEnabled, setMobileServicesEnabled] = useState(false);
  useEffect(() => {
    axios.get(`${API}/platform/config`).then((r) => {
      setMobileServicesEnabled(!!r.data?.mobile_services_enabled);
    }).catch(() => {});
  }, []);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [serviceRadiusMiles, setServiceRadiusMiles] = useState(25);
  const [remember, setRemember] = useState(() => getRememberMe());
  const [submitting, setSubmitting] = useState(false);
  // ---- AI category suggestion (opt-in) ----
  // The user clicks "Suggest Category" — we call the AI endpoint, show the
  // result and let them either accept it or pick from the full list manually.
  // If the user never touches this, the backend still auto-derives a category
  // from the description (existing behaviour). Nothing is auto-assigned in the
  // UI — `chosenCategory` stays null until the user explicitly clicks a button.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { category:{id,name}|null, confidence, reason }
  const [chosenCategory, setChosenCategory] = useState(null); // { id, name } | null
  // Iteration 80 — specialties for the chosen canonical category (stable ids).
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [chosenSpecialties, setChosenSpecialties] = useState([]);
  // Iteration 83 — default booking method. "instant" keeps today's behaviour.
  const [bookingMethod, setBookingMethod] = useState("instant");
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [challenge, setChallenge] = useState(null); // { challenge_id, channels, email_masked, reason, dev_code? }
  const [code, setCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const { login, verifyLoginChallenge, signup } = useAuth();
  const nav = useNavigate();

  // Countdown ticker for the Resend button
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Compute age from DOB (whole years). Returns null if invalid.
  const computedAge = React.useMemo(() => {
    if (!dateOfBirth) return null;
    const d = new Date(dateOfBirth + "T00:00:00");
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
    return a;
  }, [dateOfBirth]);

  const needsGuardian = computedAge !== null && computedAge >= 13 && computedAge < 18;
  const businessNeedsGuardian = role === "business" && needsGuardian;
  const dobBlocksSignup = computedAge !== null && computedAge < 13;

  // Today (YYYY-MM-DD) so <input type="date"> can cap the max value
  const maxDobToday = React.useMemo(() => new Date().toISOString().slice(0, 10), []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isSignup) {
        if (dobBlocksSignup) {
          toast.error("You must be at least 13 years old to create a BookFreely account.");
          setSubmitting(false);
          return;
        }
        if (needsGuardian && (!guardianName.trim() || !guardianEmail.trim())) {
          toast.error("Please add your parent or legal guardian's full name and email.");
          setSubmitting(false);
          return;
        }
        // Website is optional for businesses, but if provided must be a real URL
        let normalizedWebsite = "";
        if (role === "business" && website.trim()) {
          const check = validateBusinessWebsite(website);
          if (!check.ok) {
            toast.error(check.error);
            setSubmitting(false);
            return;
          }
          normalizedWebsite = check.normalized;
        }
        const u = await signup({
          email, password, full_name: fullName, role,
          date_of_birth: dateOfBirth,
          guardian_full_name: needsGuardian ? guardianName.trim() : undefined,
          guardian_email: needsGuardian ? guardianEmail.trim() : undefined,
          business_name: role === "business" ? businessName : undefined,
          business_description: role === "business" ? businessDescription : undefined,
          business_category: role === "business" && chosenCategory?.name ? chosenCategory.name : undefined,
          business_specialties: role === "business" && chosenSpecialties.length ? chosenSpecialties : undefined,
          booking_method: role === "business" ? bookingMethod : undefined,
          contact_phone: role === "business" ? contactPhone : undefined,
          contact_email: role === "business" ? (contactEmail || email) : undefined,
          website: role === "business" ? (normalizedWebsite || undefined) : undefined,
          preferred_name: role === "business" && preferredName.trim() ? preferredName.trim() : undefined,
          // Simplified location model
          provides_mobile_services: role === "business" ? providesMobile : undefined,
          has_storefront: role === "business" ? (providesMobile ? hasStorefront : true) : undefined,
          address: role === "business" && (!providesMobile || hasStorefront) ? address : undefined,
          city: role === "business" ? city : undefined,
          state: role === "business" ? stateVal : undefined,
          zip_code: role === "business" && (!providesMobile || hasStorefront) ? zipCode : undefined,
          service_radius_miles: role === "business" && providesMobile ? Number(serviceRadiusMiles) : undefined,
          phone: phone || undefined,
        });
        toast.success(`Welcome, ${u.full_name}`);
        if (u.role === "business") {
          if (u.business_category && u.business_category !== "Other") {
            toast.success(`Your business is listed under: ${u.business_category}`);
          }
          nav("/business/subscribe");
        } else {
          nav("/services");
        }
      } else {
        // Persist the "remember me" preference so it pre-checks on the next visit
        setRememberMe(remember);
        const result = await login(email, password, { remember_me: remember });
        if (result && result.challenge) {
          setChallenge(result.challenge);
          toast.success("We sent a verification code to your email");
          if (result.challenge.dev_code) {
            toast.info(`Dev code: ${result.challenge.dev_code}`);
          }
          return;
        }
        const u = result;
        toast.success("Welcome back");
        // Honour ?next= only when the user's role can actually access it,
        // otherwise fall back to their role-based landing page. Prevents the
        // "Only Super Admins can access this page" 403 when a customer or
        // business follows a stale /login?next=/admin/... link.
        nav(postLoginRedirect(u, params.get("next")));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // FastAPI validation errors come as an array — extract the first message
      let msg = "Something went wrong";
      if (typeof detail === "string" && detail.trim()) {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = `${detail[0].loc?.slice(-1)[0] || "Field"}: ${detail[0].msg}`;
      } else if (err?.message && !err.response) {
        msg = "Could not reach the server — check your connection and try again.";
      }
      // Extra friendly hint when a user tries to sign up with an existing email
      if (isSignup && /already registered/i.test(msg)) {
        toast.error(msg + " — try signing in instead.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- AI Category Suggestion handlers ---
  const requestCategorySuggestion = async () => {
    const desc = businessDescription.trim();
    if (desc.length < 10) {
      toast.error("Please describe your business first (at least 10 characters).");
      return;
    }
    setSuggesting(true);
    setSuggestion(null);
    try {
      const r = await axios.post(`${API}/categories/suggest`, {
        business_description: desc,
        business_name: businessName.trim(),
      });
      setSuggestion(r.data || null);
      if (!r.data?.category) {
        // Low confidence — nudge the user to pick manually
        setShowManualPicker(true);
        loadCategoryList();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't fetch a suggestion — please pick a category manually.");
      setShowManualPicker(true);
      loadCategoryList();
    } finally {
      setSuggesting(false);
    }
  };

  const loadCategoryList = async () => {
    if (categoriesLoaded) return;
    try {
      const r = await axios.get(`${API}/categories`);
      setCategoryList(r.data?.categories || []);
      setCategoriesLoaded(true);
    } catch (err) {
      // Non-blocking — user can still submit; backend will auto-derive.
    }
  };

  const acceptSuggestedCategory = () => {
    if (suggestion?.category) {
      setChosenCategory(suggestion.category);
      setShowManualPicker(false);
      toast.success(`Category set to "${suggestion.category.name}"`);
    }
  };

  const openManualPicker = () => {
    setShowManualPicker(true);
    loadCategoryList();
  };

  const pickCategoryManually = (cat) => {
    setChosenCategory(cat);
    toast.success(`Category set to "${cat.name}"`);
  };

  const clearChosenCategory = () => {
    setChosenCategory(null);
  };

  // Load specialty options whenever the confirmed category changes. Nothing is
  // preselected — the business ticks what applies.
  useEffect(() => {
    const name = chosenCategory?.name;
    if (!name) { setSpecialtyOptions([]); setChosenSpecialties([]); return; }
    axios.get(`${API}/specialties?category=${encodeURIComponent(name)}`)
      .then((r) => { setSpecialtyOptions(r.data?.options || []); setChosenSpecialties([]); })
      .catch(() => { setSpecialtyOptions([]); setChosenSpecialties([]); });
  }, [chosenCategory?.name]);

  const verifyChallenge = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error("Enter the code from your email"); return; }
    setSubmitting(true);
    try {
      const u = await verifyLoginChallenge({ challenge_id: challenge.challenge_id, code: code.trim() });
      toast.success("Welcome back");
      // Same role-safe redirect as submitEmail — never dump a customer /
      // business onto a super_admin-only route just because ?next= says so.
      nav(postLoginRedirect(u, params.get("next")));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resendChallengeCode = async () => {
    if (!challenge?.challenge_id || resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      const r = await axios.post(`${API}/auth/login/verify/resend`, { challenge_id: challenge.challenge_id });
      const cooldown = Number(r.data?.cooldown_seconds || 60);
      setResendCooldown(cooldown);
      setCode("");
      toast.success("A fresh code is on its way — check your email.");
      if (r.data?.dev_code) toast.info(`Dev code: ${r.data.dev_code}`);
      if (r.data?.dev_code) setChallenge((c) => c ? { ...c, dev_code: r.data.dev_code } : c);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code");
      // If server rejects because of its own cooldown, kick off a client-side timer anyway
      const msg = err?.response?.data?.detail || "";
      const m = /wait\s+(\d+)s/i.exec(msg);
      if (m) setResendCooldown(parseInt(m[1], 10) || 30);
    } finally {
      setResending(false);
    }
  };

  const inputCls = "mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]";
  const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>{isSignup ? "Create account" : "Welcome back"}</div>
        <h1 className="font-display text-4xl mt-2" style={{ color: "var(--gf-text)" }}>{isSignup ? "Join BookFreely" : "Sign in"}</h1>

        {/* Social sign-in — Google OAuth. Shown on both /login and /signup
            per user directive; role picker inside the button handles new-signup
            role selection before we hand off to Google. */}
        {GOOGLE_SIGNIN_ENABLED && !challenge && (
          <div className="mt-6" data-testid="google-auth-block">
            <GoogleAuthButton
              initialRole={role}
              label={isSignup ? "Continue with Google" : "Sign in with Google"}
              data-testid={isSignup ? "google-signup-btn" : "google-login-btn"}
            />
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1" style={{ background: "var(--gf-border)" }} />
              <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>or use email</span>
              <div className="h-px flex-1" style={{ background: "var(--gf-border)" }} />
            </div>
          </div>
        )}

        {isSignup && (
          <div className="mt-6 grid grid-cols-2 gap-2 rounded-full border p-1" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)" }}>
            <button type="button" data-testid="role-customer-btn" onClick={() => setRole("customer")}
              className="py-2 rounded-full text-sm transition-colors"
              style={{ background: role === "customer" ? "var(--gf-accent)" : "transparent", color: role === "customer" ? "var(--gf-on-accent)" : "var(--gf-text-soft)" }}>
              I'm a customer
            </button>
            <button type="button" data-testid="role-business-btn" onClick={() => setRole("business")}
              className="py-2 rounded-full text-sm transition-colors"
              style={{ background: role === "business" ? "var(--gf-accent)" : "transparent", color: role === "business" ? "var(--gf-on-accent)" : "var(--gf-text-soft)" }}>
              I'm a business
            </button>
          </div>
        )}

        {isSignup && role === "business" && (
          <div data-testid="business-fee-callout" className="mt-6 rounded-2xl border p-5 text-sm"
               style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)" }}>
            <div className="uppercase text-xs tracking-[0.2em]" style={{ color: "var(--gf-accent)" }}>Pricing</div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-center">
              <div
                data-testid="pricing-card-monthly"
                className="rounded-lg border p-3"
                style={{ borderColor: "var(--gf-border)" }}
              >
                <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>$19.99<span className="text-xs ml-1" style={{ color: "var(--gf-text-muted)" }}>/mo</span></div>
                <div className="text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--gf-text-muted)" }}>Monthly</div>
                <div className="text-[10px] mt-1" style={{ color: "var(--gf-accent)" }}>7-day free trial</div>
              </div>
              <div
                data-testid="pricing-card-yearly"
                className="rounded-lg border-2 p-3"
                style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface)" }}
              >
                <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>$199.99</div>
                <div className="text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--gf-accent)" }}>Per year</div>
                <div className="text-[10px] mt-1" style={{ color: "var(--gf-text-muted)" }}>Billed annually &middot; no trial</div>
              </div>
            </div>
            <div className="mt-3" style={{ color: "var(--gf-text-soft)" }}>
              <strong style={{ color: "var(--gf-text)" }}>0% commission</strong> on your own and returning customers. When BookFreely&rsquo;s marketplace brings you a brand-new customer, a <strong style={{ color: "var(--gf-text)" }}>one-time 10% acquisition fee</strong> (capped at <strong style={{ color: "var(--gf-text)" }}>$30</strong>) applies to that customer&rsquo;s first booking only — every booking after that is 0%. Customers pay a one-time booking &amp; processing fee of <strong style={{ color: "var(--gf-text)" }}>3.85% of the full service price</strong> at checkout.
            </div>
            <div className="mt-2 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
              You&rsquo;ll choose your plan on the next screen. Once your subscription is active, your published services can accept Instant Bookings or booking requests. Stripe payout setup is not required to appear or accept bookings, but you must complete it to receive your earnings and display the Verified badge. Until then, your earnings are held securely for you.
            </div>
          </div>
        )}

        {challenge ? (
          <form data-testid="challenge-form" onSubmit={verifyChallenge} className="mt-6 space-y-4">
            <div className="rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Extra verification</div>
              <h3 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>Check your email for a code</h3>
              <p className="mt-2 text-sm" style={{ color: "var(--gf-text-soft)" }}>
                We sent a 6-digit code to <b style={{ color: "var(--gf-text)" }}>{challenge.email_masked}</b>. Enter it below to finish signing in.
              </p>
            </div>
            <div>
              <label htmlFor="challenge-code" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Verification code</label>
              <input
                id="challenge-code"
                data-testid="challenge-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                className={`${inputCls} tracking-[8px] font-mono text-lg`}
                style={inputStyle}
              />
            </div>
            <button
              data-testid="challenge-submit-btn"
              disabled={submitting}
              className="w-full rounded-md font-medium py-2.5 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {submitting ? "Verifying…" : "Verify & sign in"}
            </button>
            <button
              type="button"
              data-testid="challenge-resend-btn"
              disabled={resendCooldown > 0 || resending}
              onClick={resendChallengeCode}
              className="w-full text-sm py-2 border rounded-md disabled:opacity-60"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              {resending
                ? "Sending…"
                : resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : "Resend code"}
            </button>
            <button
              type="button"
              data-testid="challenge-back-btn"
              onClick={() => { setChallenge(null); setCode(""); setResendCooldown(0); }}
              className="w-full text-sm underline"
              style={{ color: "var(--gf-text-muted)" }}
            >
              Use a different account
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          {isSignup && (
            <div>
              <label htmlFor="signup-fullname" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Full name</label>
              <input id="signup-fullname" data-testid="signup-fullname-input" required autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} style={inputStyle} />
            </div>
          )}
          {isSignup && (
            <div>
              <label htmlFor="signup-dob" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Date of birth</label>
              <input
                id="signup-dob"
                data-testid="signup-dob-input"
                type="date"
                required
                autoComplete="bday"
                max={maxDobToday}
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className={inputCls}
                style={inputStyle}
              />
              <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                Required. Kept private and used only to confirm you meet BookFreely's minimum-age policy. Once set, only <a href="mailto:bookfreely.support@gmail.com" className="underline">support</a> can update it — please double-check before continuing.
              </p>
              {dobBlocksSignup && (
                <p data-testid="signup-dob-under13" className="text-[12px] mt-2" style={{ color: "#dc2626" }}>
                  You must be at least 13 years old to create a BookFreely account.
                </p>
              )}
              {needsGuardian && !dobBlocksSignup && (
                <div data-testid="signup-guardian-block" className="mt-3 rounded-2xl border p-4 space-y-3"
                     style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
                  <div>
                    <div className="uppercase text-[10px] tracking-[0.22em]" style={{ color: "var(--gf-accent)" }}>Guardian required</div>
                    <div className="font-display text-lg mt-0.5" style={{ color: "var(--gf-text)" }}>
                      Because you're under 18…
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: "var(--gf-text-soft)" }}>
                      {role === "business"
                        ? "A parent or legal guardian must supervise your business account. They will be responsible for identity verification, taxes, payouts, and compliance with BookFreely's Terms."
                        : "A parent or legal guardian must be on file. We may contact them if there's a safety concern with a booking."}
                    </p>
                  </div>
                  <div>
                    <label htmlFor="signup-guardian-name" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Guardian's full name</label>
                    <input
                      id="signup-guardian-name"
                      data-testid="signup-guardian-name"
                      required={needsGuardian}
                      autoComplete="off"
                      value={guardianName}
                      onChange={(e) => setGuardianName(e.target.value)}
                      className={inputCls}
                      style={inputStyle}
                      placeholder="Parent / legal guardian's full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="signup-guardian-email" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Guardian's email</label>
                    <input
                      id="signup-guardian-email"
                      data-testid="signup-guardian-email"
                      type="email"
                      required={needsGuardian}
                      autoComplete="off"
                      value={guardianEmail}
                      onChange={(e) => setGuardianEmail(e.target.value)}
                      className={inputCls}
                      style={inputStyle}
                      placeholder="parent@example.com"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {isSignup && role === "business" && (
            <>
              <div>
                <label htmlFor="signup-businessname" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Business name</label>
                <input id="signup-businessname" data-testid="signup-businessname-input" required autoComplete="organization" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label htmlFor="signup-business-description" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                  Describe your business
                </label>
                <textarea
                  id="signup-business-description"
                  data-testid="signup-business-description"
                  required
                  rows={3}
                  autoComplete="off"
                  value={businessDescription}
                  onChange={(e) => {
                    setBusinessDescription(e.target.value);
                    // Description changed → previous AI suggestion is stale
                    if (suggestion) setSuggestion(null);
                  }}
                  placeholder="Describe what your business does."
                  className={inputCls}
                  style={inputStyle}
                />
                <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
                  We'll automatically place your business in the matching category so customers can find you.
                </p>

                {/* --- AI Category Suggestion (optional) --- */}
                <div className="mt-3 space-y-3" data-testid="ai-category-suggest-block">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid="ai-suggest-category-btn"
                      onClick={requestCategorySuggestion}
                      disabled={suggesting || businessDescription.trim().length < 10}
                      className="text-xs uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        borderColor: "var(--gf-accent)",
                        color: "var(--gf-accent)",
                        background: "transparent",
                      }}
                    >
                      {suggesting ? "Thinking…" : "✨ Suggest Category"}
                    </button>
                    {!showManualPicker && !chosenCategory && (
                      <button
                        type="button"
                        data-testid="ai-choose-manually-btn"
                        onClick={openManualPicker}
                        className="text-xs underline"
                        style={{ color: "var(--gf-text-muted)" }}
                      >
                        or choose a category manually
                      </button>
                    )}
                    {chosenCategory && (
                      <div
                        data-testid="ai-chosen-category-pill"
                        className="text-xs rounded-full px-3 py-1 border flex items-center gap-2"
                        style={{
                          borderColor: "var(--gf-accent)",
                          background: "var(--gf-surface-2)",
                          color: "var(--gf-text)",
                        }}
                      >
                        <span>Category: <strong>{chosenCategory.name}</strong></span>
                        <button
                          type="button"
                          data-testid="ai-clear-category-btn"
                          onClick={clearChosenCategory}
                          className="opacity-70 hover:opacity-100"
                          aria-label="Clear chosen category"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>

                  {/* AI suggestion result card */}
                  {suggestion && suggestion.category && (
                    <div
                      data-testid="ai-suggestion-card"
                      className="rounded-2xl border p-4"
                      style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                            AI Suggestion · {suggestion.confidence} confidence
                          </div>
                          <div
                            data-testid="ai-suggested-category-name"
                            className="font-display text-lg mt-1"
                            style={{ color: "var(--gf-text)" }}
                          >
                            {suggestion.category.name}
                          </div>
                          {suggestion.reason && (
                            <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
                              {suggestion.reason}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          data-testid="ai-use-suggested-btn"
                          onClick={acceptSuggestedCategory}
                          className="text-xs uppercase tracking-[0.15em] rounded-full px-4 py-2 transition-colors"
                          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                        >
                          Use Suggested Category
                        </button>
                        <button
                          type="button"
                          data-testid="ai-choose-another-btn"
                          onClick={openManualPicker}
                          className="text-xs uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-colors"
                          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                        >
                          Choose Another Category
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Low-confidence / no-match fallback */}
                  {suggestion && !suggestion.category && (
                    <div
                      data-testid="ai-suggestion-lowconf"
                      className="rounded-2xl border p-4 text-xs"
                      style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-muted)" }}
                    >
                      {suggestion.reason || "We couldn't confidently determine your category. Please choose one manually."}
                    </div>
                  )}

                  {/* Manual picker (dropdown of approved categories) */}
                  {showManualPicker && (
                    <div data-testid="ai-manual-picker">
                      <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                        Choose a category
                      </label>
                      <select
                        data-testid="ai-manual-category-select"
                        value={chosenCategory?.id || ""}
                        onChange={(e) => {
                          const c = categoryList.find((x) => x.id === e.target.value);
                          if (c) pickCategoryManually(c);
                        }}
                        className={inputCls}
                        style={inputStyle}
                      >
                        <option value="" disabled>
                          {categoriesLoaded ? "Select a category…" : "Loading categories…"}
                        </option>
                        {categoryList.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div data-testid="signup-booking-method" className="pt-1">
                    <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                      How should customers book you?
                    </label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[["instant", "Instant booking", "They pick an open slot and pay right away."],
                        ["request", "Request booking", "They propose a time. Nothing is charged until you accept."]].map(([v, label, hint]) => (
                        <button key={v} type="button" data-testid={`signup-booking-method-${v}`}
                                onClick={() => setBookingMethod(v)}
                                className="text-left rounded-xl border px-3 py-2 flex-1 min-w-[190px]"
                                style={{
                                  borderColor: bookingMethod === v ? "var(--gf-accent)" : "var(--gf-border-strong)",
                                  background: bookingMethod === v ? "var(--gf-bg-soft)" : "transparent",
                                }}>
                          <div className="text-xs" style={{ color: "var(--gf-text)" }}>{label}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{hint}</div>
                        </button>
                      ))}
                    </div>
                    <div className="text-[11px] mt-2" style={{ color: "var(--gf-text-muted)" }}>
                      You can change this any time in your dashboard.
                    </div>
                  </div>

                  {specialtyOptions.length > 0 && (
                    <div data-testid="signup-specialty-block" className="pt-1">
                      <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                        What do you specialize in? <span className="opacity-70 normal-case tracking-normal">(pick all that apply — optional)</span>
                      </label>
                      <div className="mt-2">
                        <SpecialtyPicker options={specialtyOptions} value={chosenSpecialties}
                                         onChange={setChosenSpecialties} testIdPrefix="signup-specialty" />
                      </div>
                      <div className="text-[11px] mt-2" style={{ color: "var(--gf-text-muted)" }}>
                        We use this to suggest the right booking questions later. You can change it any time.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="signup-contact-phone" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Contact phone <span className="opacity-70 normal-case tracking-normal text-[10px]">(shown to customers)</span></label>
                <input id="signup-contact-phone" data-testid="signup-contact-phone" type="tel" autoComplete="tel" required value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className={inputCls} style={inputStyle} placeholder="(555) 123-4567" />
              </div>
              <div>
                <label htmlFor="signup-contact-email" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Contact email <span className="opacity-70 normal-case tracking-normal text-[10px]">(shown to customers — defaults to your login email)</span></label>
                <input id="signup-contact-email" data-testid="signup-contact-email" type="email" autoComplete="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="hello@your-business.com" />
              </div>
              <div>
                <label htmlFor="signup-website" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Website <span className="opacity-70 normal-case tracking-normal text-[10px]">(optional — must be a full URL, e.g. https://your-business.com)</span></label>
                <input
                  id="signup-website"
                  data-testid="signup-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className={inputCls}
                  style={inputStyle}
                  placeholder="https://your-business.com"
                  inputMode="url"
                  type="url"
                  autoComplete="url"
                />
                <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                  Leave blank if you don&rsquo;t have a public site. Please enter a full URL — social handles (e.g. &ldquo;@instagram&rdquo;) aren&rsquo;t valid website addresses.
                </p>
              </div>
              <div>
                <label htmlFor="signup-preferred-name" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Preferred name <span className="opacity-70 normal-case tracking-normal text-[10px]">(optional — what customers see)</span></label>
                <input id="signup-preferred-name" data-testid="signup-preferred-name" autoComplete="nickname" value={preferredName} onChange={(e) => setPreferredName(e.target.value)} className={inputCls} style={inputStyle} placeholder='e.g. "Alex" instead of "Alexandra Marie Smith"' />
                <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                  Your legal name is kept private and used only for verification.
                </p>
              </div>

              {/* Mobile-services question — this drives the whole location UX below */}
              {mobileServicesEnabled ? (
                <div className="rounded-2xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
                <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Do you provide mobile / on-site services?</label>
                <p className="text-[11px] mt-1 mb-3" style={{ color: "var(--gf-text-muted)" }}>
                  Answer &quot;Yes&quot; if you travel to your customers (mobile detailer, in-home stylist, etc.).
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="signup-mobile-no"
                    onClick={() => { setProvidesMobile(false); setHasStorefront(true); }}
                    className="flex-1 rounded-md border py-2 text-sm font-medium"
                    style={{
                      borderColor: !providesMobile ? "var(--gf-accent)" : "var(--gf-border-strong)",
                      background: !providesMobile ? "var(--gf-accent)" : "transparent",
                      color: !providesMobile ? "var(--gf-on-accent)" : "var(--gf-text)",
                    }}
                  >
                    No — I work from a fixed location
                  </button>
                  <button
                    type="button"
                    data-testid="signup-mobile-yes"
                    onClick={() => { setProvidesMobile(true); }}
                    className="flex-1 rounded-md border py-2 text-sm font-medium"
                    style={{
                      borderColor: providesMobile ? "var(--gf-accent)" : "var(--gf-border-strong)",
                      background: providesMobile ? "var(--gf-accent)" : "transparent",
                      color: providesMobile ? "var(--gf-on-accent)" : "var(--gf-text)",
                    }}
                  >
                    Yes — I travel to customers
                  </button>
                </div>

                {providesMobile && (
                  <div className="mt-3">
                    <label className="flex items-center gap-2 text-sm select-none" data-testid="signup-also-storefront">
                      <input
                        type="checkbox"
                        checked={hasStorefront}
                        onChange={(e) => setHasStorefront(e.target.checked)}
                      />
                      <span style={{ color: "var(--gf-text-soft)" }}>I also have a physical storefront customers can visit</span>
                    </label>
                  </div>
                )}
              </div>
              ) : (
                // Iteration 55 — Mobile Services temporarily disabled.
                // Businesses default to a fixed location for signup today;
                // the mobile toggle will return once Mobile Services launches.
                null
              )}

              {/* Location fields — beginner-friendly, driven by the toggles above */}
              {(!providesMobile || hasStorefront) && (
                <div className="space-y-3" data-testid="signup-storefront-fields">
                  <div>
                    <label htmlFor="signup-address" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Business address</label>
                    <input id="signup-address" data-testid="signup-address" required autoComplete="street-address" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} style={inputStyle} placeholder="123 Main St" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input aria-label="City" data-testid="signup-city" required autoComplete="address-level2" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} style={inputStyle} />
                    <input aria-label="State" data-testid="signup-state" required autoComplete="address-level1" placeholder="State" maxLength={2} value={stateVal} onChange={(e) => setStateVal(e.target.value.toUpperCase())} className={inputCls} style={inputStyle} />
                    <input aria-label="ZIP" data-testid="signup-zip" required autoComplete="postal-code" placeholder="ZIP" maxLength={10} value={zipCode} onChange={(e) => setZipCode(e.target.value)} className={inputCls} style={inputStyle} />
                  </div>
                  <p className="text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
                    We'll show your city and state on your public profile and use your ZIP to calculate customer distance automatically.
                  </p>
                </div>
              )}

              {providesMobile && (
                <div className="space-y-3" data-testid="signup-mobile-fields">
                  {!hasStorefront && (
                    <div className="grid grid-cols-2 gap-2">
                      <input data-testid="signup-city-mobile" required placeholder="Home city" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} style={inputStyle} />
                      <input data-testid="signup-state-mobile" required placeholder="State" maxLength={2} value={stateVal} onChange={(e) => setStateVal(e.target.value.toUpperCase())} className={inputCls} style={inputStyle} />
                    </div>
                  )}
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Service radius</label>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {[10, 25, 50, 100].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`signup-radius-${n}`}
                          onClick={() => setServiceRadiusMiles(n)}
                          className="rounded-full px-3 py-1.5 text-xs border"
                          style={{
                            borderColor: Number(serviceRadiusMiles) === n ? "var(--gf-accent)" : "var(--gf-border-strong)",
                            background: Number(serviceRadiusMiles) === n ? "var(--gf-accent)" : "transparent",
                            color: Number(serviceRadiusMiles) === n ? "var(--gf-on-accent)" : "var(--gf-text)",
                          }}
                        >
                          {n} miles
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] mt-2" style={{ color: "var(--gf-text-muted)" }}>
                      Your exact address stays private. Customers will see &quot;Mobile service · within {serviceRadiusMiles} miles&quot; on your profile.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
          <div>
            <label htmlFor="auth-email" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Email</label>
            <input id="auth-email" data-testid="auth-email-input" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <label htmlFor="auth-password" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Password</label>
              {!isSignup && (
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs hover:underline"
                  style={{ color: "var(--gf-accent)" }}
                >
                  Forgot password?
                </Link>
              )}
            </div>
            <PasswordInput id="auth-password" data-testid="auth-password-input" required minLength={isSignup ? 8 : 6} autoComplete={isSignup ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} style={inputStyle} />
            {isSignup && (
              <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                Use 8+ characters with upper, lower, a number, and a special character.
              </p>
            )}
          </div>
          {!isSignup && (
            <label className="flex items-center gap-2 text-sm select-none">
              <input
                type="checkbox"
                data-testid="auth-remember-me"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span style={{ color: "var(--gf-text-soft)" }}>
                Remember me
              </span>
            </label>
          )}
          {isSignup && (
            <div>
              <label htmlFor="signup-phone" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Phone <span className="opacity-50 normal-case tracking-normal text-[10px]">(optional, for reminders)</span></label>
              <input id="signup-phone" data-testid="signup-phone-input" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} style={inputStyle} />
            </div>
          )}

          {isSignup && (
            <p className="text-xs" style={{ color: "var(--gf-text-muted)" }}>
              By continuing you agree to BookFreely's{" "}
              <Link to="/terms" className="hover:underline" style={{ color: "var(--gf-accent)" }}>Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="hover:underline" style={{ color: "var(--gf-accent)" }}>Privacy Policy</Link>.
            </p>
          )}

          <button data-testid="auth-submit-btn" disabled={submitting}
            className="w-full rounded-full font-medium py-3 transition-colors disabled:opacity-60"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            {submitting ? "..." : isSignup ? "Create account" : "Sign in"}
          </button>
        </form>
        )}

        <div className="mt-6 text-sm text-center" style={{ color: "var(--gf-text-muted)" }}>
          {isSignup ? (
            <>Already have an account? <Link className="hover:underline" style={{ color: "var(--gf-text)" }} to="/login">Sign in</Link></>
          ) : (
            <>New here? <Link className="hover:underline" style={{ color: "var(--gf-text)" }} to="/signup">Create an account</Link></>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
